import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { INQUIRY_OPTIONS } from "@/data/content";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls =
  "w-full bg-paper border border-line rounded-md px-4 py-3 text-ink placeholder:text-txtmut/50 focus:outline-none focus:ring-2 focus:ring-terra/60 focus:border-transparent transition-shadow";

export default function InquiryForm({ defaultInquiry = "author-visit", idPrefix = "booking", testid = "booking-form" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    inquiry_type: defaultInquiry,
    message: "",
  });
  const [state, setState] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    try {
      await submitContact(form);
      setState("success");
      toast.success("Message sent — Jen will get back to you soon.");
    } catch (err) {
      setState("idle");
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (state === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-goldsoft border border-line rounded-lg p-8 sm:p-10 text-center"
        data-testid={`${testid}-success`}
      >
        <CheckCircle2 className="w-10 h-10 text-terra mx-auto mb-4" />
        <h3 className="font-serif text-2xl text-ink mb-3">Thank you — message received.</h3>
        <p className="text-txtmut text-sm leading-relaxed mb-6 max-w-md mx-auto">
          Once you hit Send, Jen gets back to you as soon as she can. Looking forward to connecting!
          <span className="block mt-2 font-serif italic text-ink">— Jen Cullerton Johnson</span>
        </p>
        <button
          onClick={() => {
            setState("idle");
            setForm({ name: "", email: "", organization: "", inquiry_type: defaultInquiry, message: "" });
          }}
          className="font-mono text-xs uppercase tracking-[0.18em] text-terra link-underline"
          data-testid={`${testid}-send-another`}
        >
          Send another message
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid={testid}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor={`${idPrefix}-name`} className="block font-mono text-[11px] uppercase tracking-[0.18em] text-txtmut mb-2">
            Name *
          </label>
          <input id={`${idPrefix}-name`} required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid={`${testid}-name-input`} />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-email`} className="block font-mono text-[11px] uppercase tracking-[0.18em] text-txtmut mb-2">
            Email *
          </label>
          <input id={`${idPrefix}-email`} type="email" required value={form.email} onChange={set("email")} placeholder="you@school.org" className={inputCls} data-testid={`${testid}-email-input`} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor={`${idPrefix}-org`} className="block font-mono text-[11px] uppercase tracking-[0.18em] text-txtmut mb-2">
            School / Organization
          </label>
          <input id={`${idPrefix}-org`} value={form.organization} onChange={set("organization")} placeholder="Optional" className={inputCls} data-testid={`${testid}-org-input`} />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-inquiry`} className="block font-mono text-[11px] uppercase tracking-[0.18em] text-txtmut mb-2">
            I'm reaching out about *
          </label>
          <Select value={form.inquiry_type} onValueChange={(v) => setForm((f) => ({ ...f, inquiry_type: v }))}>
            <SelectTrigger id={`${idPrefix}-inquiry`} className="w-full bg-paper border-line rounded-md px-4 py-3 h-auto text-ink" data-testid={`${testid}-inquiry-select`}>
              <SelectValue placeholder="Choose a topic" />
            </SelectTrigger>
            <SelectContent>
              {INQUIRY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`${testid}-inquiry-option-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label htmlFor={`${idPrefix}-message`} className="block font-mono text-[11px] uppercase tracking-[0.18em] text-txtmut mb-2">
          Message *
        </label>
        <textarea id={`${idPrefix}-message`} required rows={5} value={form.message} onChange={set("message")} placeholder="Tell Jen about your students, your event, or your idea…" className={`${inputCls} resize-y`} data-testid={`${testid}-message-input`} />
      </div>
      <button
        type="submit"
        disabled={state === "sending"}
        className="inline-flex items-center justify-center gap-2 bg-terra hover:bg-terradark text-paper rounded-full px-8 py-3.5 font-medium transition-colors duration-300 disabled:opacity-60"
        data-testid={`${testid}-submit-button`}
      >
        {state === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Send message
      </button>
    </form>
  );
}
